import React, { useState, useEffect } from 'react';
import * as yup from 'yup';
import { Formik } from 'formik';
import { useAppDispatch } from '../../app/hooks';
import {
  updateEmployeeProfileSettingOnDev,
  getGlobalAccountPreferenceDataAsync
} from '../../common/GlobalSlice';
import { toAbsoluteUrl } from '../../_metronic/helpers';
import Loader from '../../common/Loader';

declare const chrome: any;

const { REACT_APP_TIMELIVE_API_URL } = process.env;
const BASE_URL = REACT_APP_TIMELIVE_API_URL || '';

const toBase64 = (str: string) => {
  return window.btoa(unescape(encodeURIComponent(str)));
};

interface ExtensionLoginProps {
  onLoginSuccess?: (userData: any) => void;
}

export const ExtensionLogin: React.FC<ExtensionLoginProps> = ({
  onLoginSuccess
}) => {
  const dispatch = useAppDispatch();

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Remembered login details
  const [serverUrl, setServerUrl] = useState('');
  const [savedEmail, setSavedEmail] = useState('');

  // Load remembered login details
  useEffect(() => {
    const loadSavedLogin = () => {
      if (
        typeof chrome !== 'undefined' &&
        chrome.storage &&
        chrome.storage.local
      ) {
        chrome.storage.local.get(
          ['baseUrl', 'userEmailAddress'],
          (result: any) => {
            setServerUrl(result?.baseUrl || '');
            setSavedEmail(result?.userEmailAddress || '');
          }
        );
      } else {
        setServerUrl(localStorage.getItem('baseUrl') || '');
        setSavedEmail(localStorage.getItem('userEmailAddress') || '');
      }
    };

    loadSavedLogin();
  }, []);

  const handleLogin = async (values: {
    serverUrl: string;
    emailAddress: string;
    password: string;
  }) => {
    try {
      setIsLoading(true);
      setErrorMessage('');

      let rawUrl = values.serverUrl.trim().replace(/\/+$/, '');

      if (
        !rawUrl.startsWith('http://') &&
        !rawUrl.startsWith('https://')
      ) {
        rawUrl = `https://${rawUrl}`;
      }

      const cleanBaseUrl = rawUrl;

      // Save Server URL so it is remembered after logout
      if (
        typeof chrome !== 'undefined' &&
        chrome.storage &&
        chrome.storage.local
      ) {
        await chrome.storage.local.set({
          baseUrl: cleanBaseUrl
        });
      }

      // Keep existing localStorage behavior
      localStorage.setItem('baseUrl', cleanBaseUrl);

      let loginEndpoint = '';

      if (
        rawUrl.includes('localhost') ||
        rawUrl.includes('127.0.0.1') ||
        rawUrl.includes('192.168.')
      ) {
        loginEndpoint = `${cleanBaseUrl}/api/V2/Account/TimeLiveLogin`;
      } else {
        const baseWithClassic = rawUrl.endsWith('/classic')
          ? rawUrl
          : `${rawUrl}/classic`;

        loginEndpoint = `${baseWithClassic}/api/V2/Account/TimeLiveLogin`;
      }

      const response = await fetch(loginEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json;charset=utf-8',
          'Authorization':
            'Basic ' +
            toBase64(
              values.emailAddress +
                ':' +
                encodeURIComponent(values.password)
            ),
          'IsReactClient': 'true'
        },
        body: JSON.stringify({
          UserName: values.emailAddress,
          Password: values.password,
          IsReact: true
        })
      });

      const json = await response.json();

      if (response.ok && !json.hasOwnProperty('message')) {
        const userObj = Array.isArray(json) ? json[0] : json;

        const token =
          userObj?.AccessToken ||
          userObj?.accessToken ||
          userObj?.api_token;

        const empId = Number(
          userObj?.AccountEmployeeId ||
            userObj?.accountEmployeeId
        );

        const accId = Number(
          userObj?.AccountId ||
            userObj?.accountId
        );

        const email =
          userObj?.EMailAddress ||
          userObj?.userEmailAddress ||
          values.emailAddress;

        if (!token) {
          setErrorMessage(
            'Invalid response from server. Access token missing.'
          );
          return;
        }

        dispatch(
          updateEmployeeProfileSettingOnDev({
            ...userObj,
            AccountEmployeeId: empId,
            AccountId: accId,
            AccountRoleId:
              userObj?.AccountRoleId ||
              userObj?.accountRoleId ||
              0,
            api_token: token,
            first_name:
              userObj?.FirstName ||
              userObj?.first_name ||
              '',
            last_name:
              userObj?.LastName ||
              userObj?.last_name ||
              '',
            EmployeeInitials:
              userObj?.EmployeeInitials ||
              userObj?.shortName ||
              '',
            HoursPerDay:
              userObj?.HoursPerDay ||
              userObj?.hoursPerDay ||
              8,
            UserInterfaceLanguage:
              userObj?.UserInterfaceLanguage ||
              'en-US',
            ProfileUrl:
              userObj?.ProfileUrl ||
              userObj?.profileURL ||
              ''
          })
        );

        dispatch(getGlobalAccountPreferenceDataAsync());

        // Existing session data
        localStorage.setItem('accessToken', token);
        localStorage.setItem(
          'accountEmployeeId',
          String(empId)
        );
        localStorage.setItem(
          'userEmailAddress',
          email
        );
        localStorage.setItem(
          'accountId',
          String(accId)
        );

        if (
          typeof chrome !== 'undefined' &&
          chrome.storage &&
          chrome.storage.local
        ) {
          await chrome.storage.local.set({
            // Session data
            accessToken: token,
            sessionAccountEmployeeId: empId,
            accountEmployeeId: empId,
            accountId: accId,
            userInfo: userObj,

            // Remembered login data
            baseUrl: cleanBaseUrl,
            userEmailAddress: email
          });

          if (onLoginSuccess) {
            onLoginSuccess(userObj);
          }
        } else {
          // Keep localStorage fallback
          localStorage.setItem(
            'baseUrl',
            cleanBaseUrl
          );
          localStorage.setItem(
            'userEmailAddress',
            email
          );

          if (onLoginSuccess) {
            onLoginSuccess(userObj);
          }
        }
      } else {
        setErrorMessage(
          json.message ||
            'Login failed. Please check your credentials.'
        );
      }
    } catch (error) {
      console.error('Login Error:', error);

      setErrorMessage(
        'Login request failed. Make sure your server URL is correct and server is running.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="d-flex flex-column h-100 justify-content-between p-6 bg-body">
      {isLoading && <Loader />}

      <Formik
        initialValues={{
          serverUrl: serverUrl || '',
          emailAddress: savedEmail || '',
          password: ''
        }}
        enableReinitialize
        validationSchema={yup.object().shape({
          serverUrl: yup
            .string()
            .matches(
              /^(https?:\/\/)?([\w.-]+)(:[0-9]+)?(\/.*)?$/,
              'Enter a valid Server URL'
            )
            .required('Server URL is required'),

          emailAddress: yup
            .string()
            .email('Invalid email')
            .required('Email is required'),

          password: yup
            .string()
            .required('Password is required')
        })}
        onSubmit={(values) => handleLogin(values)}
      >
        {({
          values,
          handleChange,
          errors,
          touched,
          handleSubmit
        }) => (
          <form
            onSubmit={handleSubmit}
            className="d-flex flex-column h-100 justify-content-between"
          >
            <div>
              <div className="text-center mb-6">
                <img
                  alt="Logo"
                  src={toAbsoluteUrl(
                    '/media/logos/LivetecsLogo.svg'
                  )}
                  className="h-35px mb-3"
                />

                <p className="text-gray-500 fs-8 m-0">
                  Optimize your business operations with TimeLive
                </p>
              </div>

              {errorMessage && (
                <div className="alert alert-danger p-3 fs-8 mb-4">
                  {errorMessage}
                </div>
              )}

              {/* Server URL */}
              <div className="mb-4">
                <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">
                  Server URL
                </label>

                <input
                  type="text"
                  placeholder="https://your-timelive-domain.com"
                  value={values.serverUrl}
                  onChange={handleChange('serverUrl')}
                  className="form-control form-control-sm fs-7"
                />

                {touched.serverUrl && errors.serverUrl && (
                  <div className="text-danger fs-9 mt-1">
                    {errors.serverUrl as string}
                  </div>
                )}
              </div>

              {/* Email */}
              <div className="mb-4">
                <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">
                  Email
                </label>

                <input
                  type="email"
                  placeholder="Email"
                  value={values.emailAddress}
                  onChange={handleChange('emailAddress')}
                  className="form-control form-control-sm fs-7"
                />

                {touched.emailAddress && errors.emailAddress && (
                  <div className="text-danger fs-9 mt-1">
                    {errors.emailAddress as string}
                  </div>
                )}
              </div>

              {/* Password */}
              <div className="mb-2 position-relative">
                <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">
                  Password
                </label>

                <div className="position-relative">
                  <input
                    type={
                      showPassword ? 'text' : 'password'
                    }
                    placeholder="Password"
                    value={values.password}
                    onChange={handleChange('password')}
                    className="form-control form-control-sm fs-7 pe-8"
                  />

                  <span
                    className="position-absolute top-50 end-0 translate-middle-y me-3 cursor-pointer text-gray-500 fs-7"
                    onClick={() =>
                      setShowPassword(!showPassword)
                    }
                  >
                    <i
                      className={`bi ${
                        showPassword
                          ? 'bi-eye-slash-fill'
                          : 'bi-eye-fill'
                      }`}
                    ></i>
                  </span>
                </div>
              </div>

              {touched.password && errors.password && (
                <div className="text-danger fs-9 mb-2">
                  {errors.password as string}
                </div>
              )}

              <div className="text-end mb-5"></div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn btn-primary btn-sm w-100 fs-7 fw-bold py-2 mt-2"
              >
                {isLoading
                  ? 'Signing In...'
                  : 'Sign In'}
              </button>
            </div>

            {/* Footer Terms */}
            <div className="text-center fs-9 text-gray-500 pt-4 border-top">
              By clicking Sign In, I agree to Livetecs{' '}
              <a
                href="https://www.livetecs.com/terms-of-use/"
                target="_blank"
                rel="noreferrer"
                className="text-gray-600 fw-bold"
              >
                Terms Of Use
              </a>{' '}
              and{' '}
              <a
                href="https://www.livetecs.com/privacy-policy/"
                target="_blank"
                rel="noreferrer"
                className="text-gray-600 fw-bold"
              >
                Privacy Policy
              </a>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default ExtensionLogin;

// import React, { useState, useEffect } from 'react';
// import * as yup from 'yup';
// import { Formik } from 'formik';
// import { useAppDispatch } from '../../app/hooks';
// import { updateEmployeeProfileSettingOnDev, getGlobalAccountPreferenceDataAsync } from '../../common/GlobalSlice';
// import { toAbsoluteUrl } from '../../_metronic/helpers';
// import Loader from '../../common/Loader';

// declare const chrome: any;

// const { REACT_APP_TIMELIVE_API_URL } = process.env;
// const BASE_URL = REACT_APP_TIMELIVE_API_URL || '';

// const toBase64 = (str: string) => {
//   return window.btoa(unescape(encodeURIComponent(str)));
// };

// interface ExtensionLoginProps {
//   onLoginSuccess?: (userData: any) => void;
// }

// export const ExtensionLogin: React.FC<ExtensionLoginProps> = ({ onLoginSuccess }) => {
//   const dispatch = useAppDispatch();
//   const [showPassword, setShowPassword] = useState(false);
//   const [isLoading, setIsLoading] = useState(false);
//   const [errorMessage, setErrorMessage] = useState('');
//   const [serverUrl, setServerUrl] = useState('');



//   useEffect(() => {
//     if (typeof chrome !== 'undefined' && chrome.storage && chrome.storage.local) {
//       chrome.storage.local.get(['baseUrl'], (result: any) => {
//         if (result && result.baseUrl) {
//           setServerUrl(result.baseUrl);
//         } else {
//           setServerUrl('');
//         }
//       });
//     } else {
//       const savedUrl = localStorage.getItem('baseUrl');
//       if (savedUrl) {
//         setServerUrl(savedUrl);
//       } else {
//         setServerUrl('');
//       }
//     }
//   }, []);

//   const handleLogin = async (values: { serverUrl: string; emailAddress: string; password: string }) => {
//   try {
//     setIsLoading(true);
//     setErrorMessage('');

//     let rawUrl = values.serverUrl.trim().replace(/\/+$/, '');
    
//     if (!rawUrl.startsWith('http://') && !rawUrl.startsWith('https://')) {
//       rawUrl = `https://${rawUrl}`;
//     }


//     const cleanBaseUrl = rawUrl; 
//     if (typeof chrome !== 'undefined' && chrome.storage && chrome.storage.local) {
//       await chrome.storage.local.set({ baseUrl: cleanBaseUrl });
//     }
//     localStorage.setItem('baseUrl', cleanBaseUrl);
//     let loginEndpoint = '';
//     if (rawUrl.includes('localhost') || rawUrl.includes('127.0.0.1') || rawUrl.includes('192.168.')) {
//       loginEndpoint = `${cleanBaseUrl}/api/V2/Account/TimeLiveLogin`;
//     } else {
//       const baseWithClassic = rawUrl.endsWith('/classic') ? rawUrl : `${rawUrl}/classic`;
//       loginEndpoint = `${baseWithClassic}/api/V2/Account/TimeLiveLogin`;
//     }
//     const response = await fetch(loginEndpoint, {
//       method: 'POST',
//       headers: {
//         'Content-Type': 'application/json;charset=utf-8',
//         'Authorization': 'Basic ' + toBase64(values.emailAddress + ':' + encodeURIComponent(values.password)),
//         'IsReactClient': 'true'
//       },
//       body: JSON.stringify({
//         UserName: values.emailAddress,
//         Password: values.password,
//         IsReact: true
//       })
//     });

//     const json = await response.json();

//     if (response.ok && !json.hasOwnProperty('message')) {
//       const userObj = Array.isArray(json) ? json[0] : json;

//       const token = userObj?.AccessToken || userObj?.accessToken || userObj?.api_token;
//       const empId = Number(userObj?.AccountEmployeeId || userObj?.accountEmployeeId);
//       const accId = Number(userObj?.AccountId || userObj?.accountId);
//       const email = userObj?.EMailAddress || userObj?.userEmailAddress || values.emailAddress;

//       if (!token) {
//         setErrorMessage('Invalid response from server. Access token missing.');
//         return;
//       }

//       dispatch(
//         updateEmployeeProfileSettingOnDev({
//           ...userObj,
//           AccountEmployeeId: empId,
//           AccountId: accId,
//           AccountRoleId: userObj?.AccountRoleId || userObj?.accountRoleId || 0,
//           api_token: token,
//           first_name: userObj?.FirstName || userObj?.first_name || '',
//           last_name: userObj?.LastName || userObj?.last_name || '',
//           EmployeeInitials: userObj?.EmployeeInitials || userObj?.shortName || '',
//           HoursPerDay: userObj?.HoursPerDay || userObj?.hoursPerDay || 8,
//           UserInterfaceLanguage: userObj?.UserInterfaceLanguage || 'en-US',
//           ProfileUrl: userObj?.ProfileUrl || userObj?.profileURL || ''
//         })
//       );
//       dispatch(getGlobalAccountPreferenceDataAsync());

//       localStorage.setItem('accessToken', token);
//       localStorage.setItem('accountEmployeeId', String(empId));
//       localStorage.setItem('userEmailAddress', email);
//       localStorage.setItem('accountId', String(accId));

//       if (typeof chrome !== 'undefined' && chrome.storage && chrome.storage.local) {
//         await chrome.storage.local.set({
//           accessToken: token,
//           sessionAccountEmployeeId: empId,
//           accountEmployeeId: empId,
//           userEmailAddress: email,
//           accountId: accId,
//           userInfo: userObj
//         });

//         if (onLoginSuccess) {
//           onLoginSuccess(userObj);
//         }
//       } else if (onLoginSuccess) {
//         onLoginSuccess(userObj);
//       }
//     } else {
//       setErrorMessage(json.message || 'Login failed. Please check your credentials.');
//     }
//   } catch (error) {
//     console.error('Login Error:', error);
//     setErrorMessage('Login request failed. Make sure your server URL is correct and server is running.');
//   } finally {
//     setIsLoading(false);
//   }
// };

//   return (
//     <div className="d-flex flex-column h-100 justify-content-between p-6 bg-body">
//       {isLoading && <Loader />}

//       <Formik
//         initialValues={{
//           serverUrl: serverUrl || '',
//           emailAddress: '',
//           password: ''
//         }}
//         enableReinitialize
//        validationSchema={yup.object().shape({
//   serverUrl: yup
//     .string()
//     .matches(
//       /^(https?:\/\/)?([\w.-]+)(:[0-9]+)?(\/.*)?$/,
//       'Enter a valid Server URL'
//     )
//     .required('Server URL is required'),
//   emailAddress: yup.string().email('Invalid email').required('Email is required'),
//   password: yup.string().required('Password is required')
// })}
//         onSubmit={(values) => handleLogin(values)}
//       >
//         {({ values, handleChange, errors, touched, isValid, handleSubmit }) => (
//           <form onSubmit={handleSubmit} className="d-flex flex-column h-100 justify-content-between">
//             <div>
//               <div className="text-center mb-6">
//                 <img
//                   alt="Logo"
//                   src={toAbsoluteUrl('/media/logos/LivetecsLogo.svg')}
//                   className="h-35px mb-3"
//                 />
//                 <p className="text-gray-500 fs-8 m-0">
//                   Optimize your business operations with TimeLive
//                 </p>
//               </div>

//               {errorMessage && (
//                 <div className="alert alert-danger p-3 fs-8 mb-4">
//                   {errorMessage}
//                 </div>
//               )}

//               <div className="mb-4">
//                 <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">Server URL</label>
//                 <input
//                   type="text"
//                   placeholder="https://your-timelive-domain.com"
//                   value={values.serverUrl}
//                   onChange={handleChange('serverUrl')}
//                   className="form-control form-control-sm fs-7"
//                 />
//                 {touched.serverUrl && errors.serverUrl && (
//                   <div className="text-danger fs-9 mt-1">{errors.serverUrl as string}</div>
//                 )}
//               </div>

//               <div className="mb-4">
//                 <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">Email</label>
//                 <input
//                   type="email"
//                   placeholder="Email"
//                   value={values.emailAddress}
//                   onChange={handleChange('emailAddress')}
//                   className="form-control form-control-sm fs-7"
//                 />
//                 {touched.emailAddress && errors.emailAddress && (
//                   <div className="text-danger fs-9 mt-1">{errors.emailAddress as string}</div>
//                 )}
//               </div>

//               <div className="mb-2 position-relative">
//                 <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">Password</label>
//                 <div className="position-relative">
//                   <input
//                     type={showPassword ? 'text' : 'password'}
//                     placeholder="Password"
//                     value={values.password}
//                     onChange={handleChange('password')}
//                     className="form-control form-control-sm fs-7 pe-8"
//                   />
//                   <span
//                     className="position-absolute top-50 end-0 translate-middle-y me-3 cursor-pointer text-gray-500 fs-7"
//                     onClick={() => setShowPassword(!showPassword)}
//                   >
//                     <i className={`bi ${showPassword ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
//                   </span>
//                 </div>
//               </div>
//               {touched.password && errors.password && (
//                 <div className="text-danger fs-9 mb-2">{errors.password as string}</div>
//               )}

//               <div className="text-end mb-5"></div>

//              <button
//   type="submit"
//   disabled={isLoading}
//   className="btn btn-primary btn-sm w-100 fs-7 fw-bold py-2 mt-2"
// >
//   {isLoading ? 'Signing In...' : 'Sign In'}
// </button>
//             </div>

//             {/* Footer Terms */}
//             <div className="text-center fs-9 text-gray-500 pt-4 border-top">
//               By clicking Sign In, I agree to Livetecs{' '}
//               <a href="https://www.livetecs.com/terms-of-use/" target="_blank" rel="noreferrer" className="text-gray-600 fw-bold">
//                 Terms Of Use
//               </a>{' '}
//               and{' '}
//               <a href="https://www.livetecs.com/privacy-policy/" target="_blank" rel="noreferrer" className="text-gray-600 fw-bold">
//                 Privacy Policy
//               </a>
//             </div>
//           </form>
//         )}
//       </Formik>
//     </div>
//   );
// };
// export default ExtensionLogin;